/**
 * 🟥 CUADRO NEGRO 2026-05-30 — Botón GitHub + LED de estado (Header).
 *
 * Ubicación: junto a "Agenda de Comunicación" (FeedbackNotesButton),
 * dentro de HeaderLogo.
 *
 * Render:
 *  - Ícono GitHub (lucide-react)
 *  - LED redondo a la derecha con 5 estados:
 *      red        → no conectado
 *      yellow     → conectado sin scope 'repo'
 *      green      → conectado correctamente
 *      greenBlink → conectado + proyecto activo cargado
 *      locked     → usuario no Premium (tooltip "Solo para Premium")
 *
 * Dos zonas de hover independientes con tooltips:
 *  - Hover sobre el ícono GitHub → tooltip "GitHub".
 *  - Hover sobre el LED          → tooltip dinámico con la instrucción.
 *
 * Click:
 *  - Premium → abre SaveToGitHubModal.
 *  - No Premium → toast + navega a /planes-y-beneficios.
 *
 * 🟥 FIX 2026-06-01: las llamadas a /api usan rutas RELATIVAS (same-origin).
 * NO usar `REACT_APP_BACKEND_URL` porque en producción Emergent ese env var
 * puede apuntar a un host distinto del que ve el navegador (preview/staging),
 * y la cookie de sesión httpOnly no se envía → `eligible:false` espurio.
 * Same-origin garantiza que damyxia.com habla con damyxia.com.
 *
 * Nunca pide ni muestra un PAT.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Github } from 'lucide-react';
import { toast } from 'sonner';
import SaveToGitHubModal from './SaveToGitHubModal';

const fetchJSON = async (path, opts = {}) => {
  // Same-origin (sin prefix host) → la cookie de sesión SIEMPRE se envía.
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

// Estados del LED — orden y textos OFICIALES (owner, 2026-05-31):
//   1) 🔴 red        → "GitHub NO conectado"
//   2) 🟡 yellow     → "Conectado pero sin permisos válidos"
//   3) ✨ greenBlink → "Conectado Correctamente"   (verde parpadeando)
//   4) 🟢 green      → "GitHub Conectado"          (verde fijo)
// Texto extra (no-Premium): "Solo para Premium" (estado `locked`).
const STATES = {
  red: {
    color: '#ef4444',
    glow: 'rgba(239, 68, 68, 0.7)',
    blink: false,
    es: 'GitHub NO conectado',
    en: 'GitHub NOT connected',
  },
  yellow: {
    color: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.7)',
    blink: false,
    es: 'Conectado pero sin permisos válidos',
    en: 'Connected but without valid permissions',
  },
  greenBlink: {
    color: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.9)',
    blink: true,
    es: 'Conectado Correctamente',
    en: 'Connected Correctly',
  },
  green: {
    color: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.7)',
    blink: false,
    es: 'GitHub Conectado',
    en: 'GitHub Connected',
  },
  locked: {
    color: '#ef4444',
    glow: 'rgba(239, 68, 68, 0.7)',
    blink: false,
    es: 'Solo para Premium',
    en: 'Premium only',
  },
};

const TOOLTIP_CLS =
  'fixed z-[9999] px-2 py-1.5 text-[11px] leading-tight font-medium text-white text-center ' +
  'bg-zinc-900/95 border border-zinc-700/70 rounded-md shadow-lg ' +
  'max-w-[220px] whitespace-normal break-words pointer-events-none ' +
  'transition-opacity duration-150';

const GitHubHeaderButton = ({ projectFiles = [], language = 'es' }) => {
  const navigate = useNavigate();
  const [eligible, setEligible] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [connected, setConnected] = useState(false);
  const [scopeOk, setScopeOk] = useState(false);
  const [open, setOpen] = useState(false);

  const [iconTip, setIconTip] = useState({ show: false, left: 0, top: 0 });
  const [ledTip, setLedTip] = useState({ show: false, left: 0, top: 0 });
  const iconRef = useRef(null);
  const ledRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const elig = await fetchJSON('/api/github/eligibility');
      const isElig = !!elig.eligible;
      setEligible(isElig);
      setEligibilityChecked(true);
      if (!isElig) {
        setConnected(false);
        setScopeOk(false);
        return;
      }
      try {
        const status = await fetchJSON('/api/github/status');
        setConnected(!!status.connected);
        setScopeOk(!!status.scope_ok);
      } catch {
        setConnected(false);
        setScopeOk(false);
      }
    } catch {
      setEligible(false);
      setEligibilityChecked(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refrescar tras callback OAuth (?github=connected&login=USER) +
  // mostrar toast verde "¡Conectado con GitHub como @USERNAME!" UNA SOLA VEZ.
  // - sessionStorage guard → blinda contra dobles ejecuciones (StrictMode/dev).
  // - setTimeout 50ms     → defiere el toast tras el mount real (sonner ready).
  // - replaceState limpia la URL para que tras un reload no vuelva a aparecer.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ghParam = params.get('github');
    if (ghParam === 'connected') {
      const login = (params.get('login') || '').trim();
      const guardKey = `damyxia.gh.toast.${login || 'unknown'}.shown`;
      const alreadyShown = sessionStorage.getItem(guardKey) === '1';
      refresh();
      if (login && !alreadyShown) {
        sessionStorage.setItem(guardKey, '1');
        // Diferimos para que el Toaster esté garantizadamente montado.
        setTimeout(() => {
          toast.success(`¡Conectado con GitHub como @${login}!`, {
            position: 'top-right',
            duration: 3500,
            className: 'gh-oauth-success-toast',
            style: {
              background: '#16a34a',
              color: '#ffffff',
              border: '2px solid #22c55e',
              fontSize: '15px',
              fontWeight: 600,
              padding: '14px 18px',
              minHeight: '60px',
              minWidth: '320px',
              boxShadow: '0 10px 30px rgba(34, 197, 94, 0.35)',
            },
          });
        }, 80);
      }
      params.delete('github');
      params.delete('login');
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash
      );
    } else if (ghParam === 'error') {
      const reason = params.get('reason') || 'unknown';
      const guardKey = `damyxia.gh.toast.error.${reason}.shown`;
      if (sessionStorage.getItem(guardKey) !== '1') {
        sessionStorage.setItem(guardKey, '1');
        setTimeout(() => {
          toast.error(`Error al conectar con GitHub: ${reason}`, {
            position: 'top-right',
            duration: 4000,
          });
        }, 80);
      }
      params.delete('github');
      params.delete('reason');
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash
      );
    }
  }, [refresh]);

  if (!eligibilityChecked) return null;

  const hasActiveProject = Array.isArray(projectFiles) && projectFiles.length > 0;

  let stateKey = 'red';
  if (!eligible) stateKey = 'locked';
  else if (connected && !scopeOk) stateKey = 'yellow';
  else if (connected && scopeOk && hasActiveProject) stateKey = 'greenBlink';
  else if (connected && scopeOk) stateKey = 'green';
  const state = STATES[stateKey];

  const handleClick = () => {
    if (!eligible) {
      toast.info(language === 'es' ? 'Solo para Premium' : 'Premium only');
      navigate('/planes-y-beneficios');
      return;
    }
    setOpen(true);
  };

  // Helpers: posicionar tooltip centrado debajo del anchor.
  const showTipFor = (ref, setter) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setter({
      show: true,
      left: r.left + r.width / 2,
      top: r.bottom + 8, // 8px gap → no tapa al anchor
    });
  };
  const hideTip = (setter) => setter((s) => ({ ...s, show: false }));

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="relative inline-flex items-center gap-1 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
        aria-label={language === 'es' ? 'GitHub — guardar proyecto' : 'GitHub — save project'}
        data-testid="github-header-btn"
        data-state={stateKey}
      >
        {/* Zona 1 — Ícono GitHub. Tooltip NATIVO del navegador (title="…")
            para que se vea bien en modo día Y modo noche, igual que el
            botón "Agenda de Comunicación y Ayuda". */}
        <span
          ref={iconRef}
          className="relative inline-flex"
          title="GitHub"
        >
          <Github className="w-4 h-4" strokeWidth={1.75} />
        </span>

        {/* Zona 2 — LED. Tooltip nativo con el mensaje dinámico del estado. */}
        <span
          ref={ledRef}
          className="relative inline-flex"
          title={state[language] || state.es}
        >
          <span
            className="block rounded-full"
            style={{
              width: '8px',
              height: '8px',
              backgroundColor: state.color,
              boxShadow: `0 0 6px ${state.glow}`,
              animation: state.blink ? 'gh-led-blink 1.2s ease-in-out infinite' : 'none',
            }}
            data-testid="github-led"
            data-led-color={stateKey === 'greenBlink' ? 'green' : stateKey}
          />
        </span>
      </button>

      {eligible && (
        <SaveToGitHubModal
          open={open}
          onClose={() => setOpen(false)}
          files={projectFiles}
          connected={connected}
          scopeOk={scopeOk}
          onRefreshStatus={refresh}
          language={language}
        />
      )}

      <style>{`
        @keyframes gh-led-blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.35; transform: scale(0.85); }
        }
      `}</style>
    </>
  );
};

export default GitHubHeaderButton;
